/**
 * Partner Finder API client.
 *
 * Backend integration for partner matching and messaging operations. Handles
 * CRUD operations for partner requests, message management, and partner search
 * functionality.
 *
 * @module partnerApi
 */

import { apiGet, apiPost, apiPut, apiDelete, apiPatch } from "./apiUtils";
import { buildQueryString } from "./utils";
import { getOrCreateKeyPair, getPrivateKey, encryptMessage, decryptMessage } from "./crypto";
import type {
  PartnerRequestAPI,
  MessageAPI,
  SessionCount,
  PartnerFilters,
} from '@/types/api/partner';
import { logger } from "./logger";

// ============================================================================
// API Functions
// ============================================================================

// Partner Request APIs

/**
 * Fetch all partner requests with optional filters.
 *
 * Retrieves partner requests from the API, optionally filtered by level, goal,
 * meeting preference, location, and search query. Filters are applied both
 * server-side (via query parameters) and client-side (for availability).
 *
 * @param filters - Optional filter criteria (level, goal, isOnline, location, searchQuery)
 * @returns Promise resolving to array of PartnerRequestAPI objects, or empty array on error
 *
 * @example
 * ```typescript
 * const requests = await getAllPartnerRequests({
 *   level: 'B1',
 *   goal: 'exam-prep',
 *   isOnline: true,
 * });
 * ```
 */
export async function getAllPartnerRequests(
  filters?: PartnerFilters,
): Promise<PartnerRequestAPI[]> {
  // Build query parameters
  const queryString = buildQueryString({
    level: filters?.level,
    goal: filters?.goal,
    is_online: filters?.isOnline,
    location: filters?.location,
    search: filters?.searchQuery,
  });

  const url = queryString ? `/partner/requests?${queryString}` : "/partner/requests";

  // Use apiGet wrapper for consistent error handling
  const data = await apiGet<{ requests?: PartnerRequestAPI[] }>(
    url,
    {
      errorMessage: "Failed to fetch partner requests",
      context: "getAllPartnerRequests",
      errorReturnValue: { requests: [] } as { requests?: PartnerRequestAPI[] },
    }
  );

  // Handle null case (shouldn't happen with errorReturnValue, but TypeScript needs it)
  if (!data) {
    return [];
  }

  return data.requests || [];
}

/**
 * Fetch partner requests for a specific user.
 *
 * Retrieves all partner requests created by the specified user, including
 * both active and paused requests.
 *
 * @param userId - User ID to fetch requests for
 * @returns Promise resolving to array of PartnerRequestAPI objects for the user, or empty array on error
 *
 * @example
 * ```typescript
 * const myRequests = await getMyPartnerRequests(userId);
 * ```
 */
export async function getMyPartnerRequests(
  userId: string,
): Promise<PartnerRequestAPI[]> {
  const result = await apiGet<{ requests?: PartnerRequestAPI[] }>(
    `/partner/requests/user/${userId}`,
    {
      errorMessage: "Failed to fetch user requests",
      context: "getMyPartnerRequests",
      errorReturnValue: { requests: [] } as { requests?: PartnerRequestAPI[] },
    }
  );

  return result?.requests || [];
}

/**
 * Create a new partner request.
 *
 * Creates a new partner request with the provided data. The request will be
 * active by default and visible to other users matching the criteria.
 *
 * @param requestData - Partial PartnerRequestAPI object with request details (level, goal, availability, etc.)
 * @returns Promise resolving to created PartnerRequestAPI object, or null on error
 *
 * @example
 * ```typescript
 * const newRequest = await createPartnerRequest({
 *   userId: 'user-123',
 *   level: 'B1',
 *   goal: 'exam-prep',
 *   isOnline: true,
 *   suggestedDays: ['Monday', 'Wednesday'],
 * });
 * ```
 */
export async function createPartnerRequest(
  requestData: Partial<PartnerRequestAPI>,
): Promise<PartnerRequestAPI | null> {
  const result = await apiPost<{ request?: PartnerRequestAPI }>(
    "/partner/requests",
    requestData,
    {
      errorMessage: "Failed to create partner request",
      context: "createPartnerRequest",
    }
  );

  if (!result?.request) {
    return null;
  }

  return result.request;
}

/**
 * Update an existing partner request.
 *
 * Updates the specified partner request with new data. Only provided fields
 * will be updated; other fields remain unchanged.
 *
 * @param id - Partner request ID to update
 * @param requestData - Partial PartnerRequestAPI object with fields to update
 * @returns Promise resolving to updated PartnerRequestAPI object, or null on error
 *
 * @example
 * ```typescript
 * const updated = await updatePartnerRequest('request-123', {
 *   level: 'B2',
 *   goal: 'conversational',
 * });
 * ```
 */
export async function updatePartnerRequest(
  id: string,
  requestData: Partial<PartnerRequestAPI>,
): Promise<PartnerRequestAPI | null> {
  const result = await apiPut<{ request?: PartnerRequestAPI }>(
    `/partner/requests/${id}`,
    requestData,
    {
      errorMessage: "Failed to update partner request",
      context: "updatePartnerRequest",
    }
  );

  if (!result?.request) {
    return null;
  }

  return result.request;
}

/**
 * Delete a partner request.
 *
 * Permanently deletes the specified partner request. This action cannot be undone.
 *
 * @param id - Partner request ID to delete
 * @returns Promise resolving to true if deletion succeeded, false otherwise
 *
 * @example
 * ```typescript
 * const success = await deletePartnerRequest('request-123');
 * if (success) {
 *   console.log('Request deleted');
 * }
 * ```
 */
export async function deletePartnerRequest(id: string): Promise<boolean> {
  const result = await apiDelete<boolean>(
    `/partner/requests/${id}`,
    {
      errorMessage: "Failed to delete partner request",
      context: "deletePartnerRequest",
      errorReturnValue: false,
    }
  );

  return result ?? false;
}

/**
 * Toggle partner request active/paused status.
 *
 * Activates or pauses a partner request. Paused requests are not visible to
 * other users but can be reactivated later.
 *
 * @param id - Partner request ID to toggle
 * @param isActive - True to activate, false to pause
 * @returns Promise resolving to true if status update succeeded, false otherwise
 *
 * @example
 * ```typescript
 * // Pause a request
 * await togglePartnerRequestStatus('request-123', false);
 *
 * // Activate a request
 * await togglePartnerRequestStatus('request-123', true);
 * ```
 */
export async function togglePartnerRequestStatus(
  id: string,
  isActive: boolean,
): Promise<boolean> {
  const result = await apiPatch<boolean>(
    `/partner/requests/${id}/toggle`,
    { isActive },
    {
      errorMessage: "Failed to update request status",
      context: "togglePartnerRequestStatus",
      errorReturnValue: false,
    }
  );

  return result ?? false;
}

// Public Key APIs

/**
 * Fetch a user's RSA-OAEP public key from the server.
 *
 * @param userId - User ID to look up
 * @returns Base64-encoded SPKI public key, or null if not yet uploaded
 */
export async function getUserPublicKey(userId: string): Promise<string | null> {
  const result = await apiGet<{ publicKey?: string | null }>(
    `/partner/users/${userId}/public-key`,
    {
      errorMessage: "Failed to fetch public key",
      context: "getUserPublicKey",
      errorReturnValue: null,
    }
  );
  return result?.publicKey ?? null;
}

/**
 * Upload the calling user's RSA-OAEP public key to the server.
 *
 * @param userId - The calling user's ID
 * @param publicKey - Base64-encoded SPKI public key
 */
export async function setUserPublicKey(userId: string, publicKey: string): Promise<void> {
  await apiPut<unknown>(
    `/partner/users/${userId}/public-key`,
    { publicKey },
    {
      errorMessage: "Failed to upload public key",
      context: "setUserPublicKey",
    }
  );
}

// Message APIs

/**
 * Fetch all messages for a specific user.
 *
 * Retrieves all messages (both sent and received) for the specified user,
 * including message metadata like sender, recipient, timestamps, and read status.
 *
 * @param userId - User ID to fetch messages for
 * @returns Promise resolving to array of MessageAPI objects, or empty array on error
 *
 * @example
 * ```typescript
 * const messages = await getMyMessages(userId);
 * ```
 */
export interface GetMyMessagesResult {
  messages: MessageAPI[];
  sessionCounts: SessionCount[];
}

export async function getMyMessages(userId: string): Promise<GetMyMessagesResult> {
  const result = await apiGet<{ messages?: MessageAPI[]; sessionCounts?: SessionCount[] }>(
    `/partner/messages/user/${userId}`,
    {
      errorMessage: "Failed to fetch messages",
      context: "getMyMessages",
      errorReturnValue: { messages: [], sessionCounts: [] },
    }
  );

  const messages = result?.messages || [];
  const sessionCounts = result?.sessionCounts || [];

  const encryptedMessages = messages.filter((m) => m.isEncrypted);
  if (encryptedMessages.length === 0) return { messages, sessionCounts };

  const privateKey = await getPrivateKey(userId);

  const decryptedMessages = await Promise.all(
    messages.map(async (msg) => {
      if (!msg.isEncrypted) return msg;

      // Pick the wrapped AES key that belongs to this user
      const encryptedKey =
        msg.fromUserId === userId
          ? msg.encryptedKeyForSender
          : msg.encryptedKeyForRecipient;

      if (!encryptedKey || !privateKey) {
        return { ...msg, content: '[Encrypted message — key not available]' };
      }

      try {
        const plaintext = await decryptMessage(msg.content, encryptedKey, privateKey);
        return { ...msg, content: plaintext };
      } catch {
        logger.warn('Failed to decrypt message', { messageId: msg.id });
        return { ...msg, content: '[Encrypted message — decryption failed]' };
      }
    }),
  );

  return { messages: decryptedMessages, sessionCounts };
}

/**
 * Send a message to another user.
 *
 * Creates and sends a new message to the specified recipient. Can optionally
 * reference a partner request or be a reply to an existing message.
 *
 * @param messageData - Message data object containing:
 *   - `fromUserId`: Sender's user ID
 *   - `toUserId`: Recipient's user ID
 *   - `content`: Message content text
 *   - `requestId`: Optional partner request ID this message relates to
 *   - `subject`: Optional message subject
 *   - `parentMessageId`: Optional parent message ID for replies
 * @returns Promise resolving to created MessageAPI object, or null on error
 *
 * @example
 * ```typescript
 * const message = await sendMessage({
 *   fromUserId: 'user-123',
 *   toUserId: 'user-456',
 *   content: 'Hello! Would you like to practice together?',
 *   requestId: 'request-789',
 * });
 * ```
 */
export interface SendMessageResult {
  message: MessageAPI;
  /** Messages the sender can still send in this session; null when not in a session. */
  messagesRemaining: number | null;
}

export async function sendMessage(messageData: {
  fromUserId: string;
  toUserId: string;
  requestId?: string;
  subject?: string;
  content: string;
  parentMessageId?: string;
}): Promise<SendMessageResult | null> {
  let payload: typeof messageData & {
    isEncrypted?: boolean;
    encryptedKeyForSender?: string;
    encryptedKeyForRecipient?: string;
  } = { ...messageData };

  try {
    // Ensure sender's key pair exists and is uploaded
    const { publicKeyB64: senderPublicKeyB64 } = await getOrCreateKeyPair(messageData.fromUserId);
    await setUserPublicKey(messageData.fromUserId, senderPublicKeyB64);

    // Fetch recipient's public key
    const recipientPublicKeyB64 = await getUserPublicKey(messageData.toUserId);

    if (recipientPublicKeyB64) {
      const { encryptedContent, encryptedKeyForSender, encryptedKeyForRecipient } =
        await encryptMessage(messageData.content, recipientPublicKeyB64, senderPublicKeyB64);

      payload = {
        ...messageData,
        content: encryptedContent,
        isEncrypted: true,
        encryptedKeyForSender,
        encryptedKeyForRecipient,
      };
    }
  } catch (err) {
    logger.warn('E2EE encryption failed, sending plain text', { error: String(err) });
    // Fall back to plain text so the message is not lost
  }

  const result = await apiPost<{ message?: MessageAPI; messagesRemaining?: number | null }>(
    "/partner/messages",
    payload,
    {
      errorMessage: "Failed to send message",
      context: "sendMessage",
    }
  );

  if (!result?.message) return null;

  return {
    // Return plain-text content so the UI is immediately consistent
    message: { ...result.message, content: messageData.content },
    messagesRemaining: result.messagesRemaining ?? null,
  };
}

/**
 * Mark a message as read.
 *
 * Updates the read status of a message to indicate it has been viewed by
 * the recipient. Used to track unread message counts.
 *
 * @param messageId - Message ID to mark as read
 * @returns Promise resolving to true if update succeeded, false otherwise
 *
 * @example
 * ```typescript
 * await markMessageAsRead('message-123');
 * ```
 */
export async function markMessageAsRead(messageId: string): Promise<boolean> {
  const result = await apiPatch<boolean>(
    `/partner/messages/${messageId}/read`,
    undefined,
    {
      errorMessage: "Failed to mark message as read",
      context: "markMessageAsRead",
      errorReturnValue: false,
    }
  );

  return result ?? false;
}

/**
 * Delete a message.
 *
 * Permanently deletes the specified message. This action cannot be undone.
 *
 * @param messageId - Message ID to delete
 * @returns Promise resolving to true if deletion succeeded, false otherwise
 *
 * @example
 * ```typescript
 * const success = await deleteMessage('message-123');
 * ```
 */
export async function deleteMessage(messageId: string): Promise<boolean> {
  const result = await apiDelete<boolean>(
    `/partner/messages/${messageId}`,
    {
      errorMessage: "Failed to delete message",
      context: "deleteMessage",
      errorReturnValue: false,
    }
  );

  return result ?? false;
}

